import { useTheme } from '../../../hooks/useTheme';
import { Card, Button, Pill } from '@dsi/react-eds';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { CnisEndpoint, EnvironmentCategory } from './cnis-endpoint-interfaces';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { useLoader } from '../../../hooks/useLoader';
import api from '../../../api/axiosInstance';
import styles from './CnisEndpointForm.module.css';
import { AxiosError } from 'axios';
import { LOADER_DEBOUNCE_MS } from '../../../const/global-constants';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';

const CnisEndpoints = () => {

    const { theme } = useTheme();
    const navigate = useNavigate();
    const { addNotifications } = useSwobNotifications();
    const { showLoader, hideLoader } = useLoader();

    const [cnisEndpoints, setCnisEndpoints] = useState<CnisEndpoint[]>([]);
    const [filteredCnisEndpoints, setFilteredCnisEndpoints] = useState<CnisEndpoint[]>([]);
    const [selectedCnisEndpoint, setSelectedCnisEndpoint] = useState<CnisEndpoint>();
    const [openDialog, setOpenDialog] = useState<boolean>(false);

    const categoryPills: EnvironmentCategory[] = ['Production', 'Staging', 'Development', 'Testing'];
    const [filteredCategories, setFilteredCategories] = useState<EnvironmentCategory[]>(categoryPills);

    // Fetch EIAP clusters from backend on component mount
    useEffect(() => {
        fetchCnisEndpoints();
    }, []);

    useEffect(() => {
        if (filteredCategories.length > 0) {
            const filtered = cnisEndpoints.filter((ce) => filteredCategories.includes(ce.category))
            setFilteredCnisEndpoints(filtered);
        } else {
            setFilteredCnisEndpoints(cnisEndpoints);
        }
    }, [filteredCategories]);

    const fetchCnisEndpoints = async () => {
        try {
            showLoader();
            const response = await api.get('/api/cnis-endpoint');
            setCnisEndpoints(response.data);
            setFilteredCnisEndpoints(response.data);
        } catch (error) {
            addNotifications([
                {
                title: 'Error - CNIS CSS Endpoints',
                icon: 'icon-triangle-warning',
                description:
                    error && error instanceof AxiosError
                    ? error.response?.data?.message || error.message
                    : 'An error occurred while fetching CNIS CSS Endpoints.',
                color: 'red',
                timestamp: new Date()
                }
            ]);
        } finally {
            setTimeout(() => { hideLoader(); }, LOADER_DEBOUNCE_MS);
        }
    };

    const handleCreateCnisEndpoint = () => {
        navigate('/integrations/cnis/new');
    };

    const handleEditCnisEndpoint = (id: string) => {
        navigate(`/integrations/cnis/edit/${id}`);
    };

    const handleDeleteCnisEndpoint = (cnisEndpoint: CnisEndpoint) => {
        setSelectedCnisEndpoint(cnisEndpoint);
        setOpenDialog(true);
    }

    const handleCancelDeleteCnisEndpoint = () => {
        setSelectedCnisEndpoint(undefined);
        setOpenDialog(false);
    }

    const deleteCnisEndpoint = async () => {
        if (selectedCnisEndpoint) {
            try {
                showLoader();
                await api.delete(`/api/cnis-endpoint/${selectedCnisEndpoint.id}`);
                addNotifications([
                    {
                    title: `${selectedCnisEndpoint.name} removed succesfully`,
                    icon: 'icon-success',
                    description: `The CNIS CSS Endpoint has been succesfully removed.`,
                    timestamp: new Date(),
                    color: 'green',
                    },
                ]);
                // Refresh the list after deletion
                await fetchCnisEndpoints();
                setOpenDialog(false);
            } catch (error) {
                addNotifications([
                    {
                    title: `Failed to remove ${selectedCnisEndpoint.name}`,
                    icon: 'icon-triangle-warning',
                    description:
                        error && error instanceof AxiosError
                        ? error.response?.data?.message || error.message
                        : 'The system was unable to remove the CNIS CSS Endpoint. Plase try again.',
                    timestamp: new Date(),
                    color: 'red',
                    },
                ]);
            } finally {
            setTimeout(() => { hideLoader(); }, LOADER_DEBOUNCE_MS);
            }
        }
    };

    const getCategoryTagClass = (category: CnisEndpoint['category']) => {
        switch (category) {
        case 'Production':
            return 'red';
        case 'Staging':
            return 'yellow';
        case 'Development':
            return 'blue';
        case 'Testing':
            return 'green';
        default:
            return 'purple';
        }
    };

  return (
    <>
        <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
            {/* Header with Create Button */}

            <div className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark}`}>
                <div className="header">
                    <span className={styles.title}>CNIS CSS</span>
                </div>
                <p className={styles.subtitle}>
                    Set up a CNIS CSS Endpoint by defining its information, connection detail and SFTP settings.
                </p>
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
                    <ReactEDSWrapper component={Button} primary onClick={handleCreateCnisEndpoint}>
                        <i className="icon icon-plus" /> Create New CNIS Endpoint
                    </ReactEDSWrapper>
                </div>

                {/* Category Filter Pills */}
                <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {categoryPills.map((category) => (
                    <ReactEDSWrapper
                    key={category}
                    component={Pill}
                    toggle
                    unselected={!filteredCategories.includes(category)}
                    onToggle={(unselected) => {
                        if (unselected) {
                        setFilteredCategories([...filteredCategories, category]);
                        } else if (filteredCategories.length === categoryPills.length) {
                        setFilteredCategories([category]);
                        } else if (filteredCategories.length === 1) {
                        setFilteredCategories(categoryPills);
                        } else {
                        setFilteredCategories(filteredCategories.filter((cat) => cat !== category));
                        }
                    }}
                    >
                    {category}
                    </ReactEDSWrapper>
                ))}
                <div style={{ color: '#6a6a6a' }}>
                    {filteredCnisEndpoints.length} CNIS Endpoint{filteredCnisEndpoints.length !== 1 ? 's' : ''}
                </div>
                {filteredCategories.length < categoryPills.length && (
                    <a
                    href="javascript:void(0);"
                    onClick={() => setFilteredCategories(categoryPills)}
                    style={{ marginLeft: '8px', fontSize: '14px' }}
                    >
                    Select all
                    </a>
                )}
                </div>

                <div className="row">
                    <div className="column sm-12">
                        <div className="row">
                            {filteredCnisEndpoints.length === 0 && 
                                <div className={`column sm-12 ${styles.noDataContainer}`}>
                                    <i className={`icon icon-info ${styles.noDataIcon}`} />
                                    <div className={styles.noDataTitle}>There is no CNIS Endpoints created</div>
                                    <p className={styles.subtitle}>
                                        Please add a new CNIS Endpoint.
                                    </p>
                                </div>
                            }
                            {filteredCnisEndpoints.map((CnisEndpoint) => (
                                <div key={CnisEndpoint.id} className={`column sm-12 md-6 xl-4 ${styles.noBorder}`}>
                                    <ReactEDSWrapper
                                    component={Card}
                                    className={styles.cardNoMargin}
                                    title={CnisEndpoint.name}
                                    >
                                        {/* Description */}
                                        <p style={{ marginBottom: '12px', color: '#6a6a6a' }}>{CnisEndpoint.description}</p>

                                        {/* Category Badge */}
                                        <div style={{ marginBottom: '12px' }}>
                                            <kbd className={`tag big ${getCategoryTagClass(CnisEndpoint.category)}`}>
                                            {CnisEndpoint.category}
                                            </kbd>
                                        </div>

                                        {/* Endpoint Badges */}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', marginBottom: '12px' }}>
                                            <ReactEDSWrapper key={`${CnisEndpoint.id}`} component={Pill}>
                                                <i className="icon icon-link" /> {CnisEndpoint.endpoint}
                                            </ReactEDSWrapper>
                                        </div>

                                        {/* Action Buttons */}
                                        <div>
                                            <ReactEDSWrapper
                                            component={Button}
                                            variant="secondary"
                                            onClick={() => CnisEndpoint.id && handleEditCnisEndpoint(CnisEndpoint.id)}
                                            >
                                            <i className="icon icon-edit" /> Edit
                                            </ReactEDSWrapper>
                                            <ReactEDSWrapper
                                            component={Button}
                                            variant="secondary"
                                            onClick={() => CnisEndpoint.id && handleDeleteCnisEndpoint(CnisEndpoint)}
                                            >
                                            <i className="icon icon-trashcan" /> Delete
                                            </ReactEDSWrapper>
                                        </div>
                                    </ReactEDSWrapper>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <ConfirmDialog 
            onConfirm={deleteCnisEndpoint} 
            onCancel={handleCancelDeleteCnisEndpoint} 
            classes='smallDialog'
            open={openDialog}
            title={'Remove CNIS CSS Endpoint'}
            subtitle={'CNIS CSS'}
            buttonText={'Remove CNIS Endpoint'}
            innerText={`You're about to remove this CNIS CSS Endpoint from the list. 
                This action is permanent and cannot be undone 
                <br /><br />
                Are you sure you want to continue?`}
        />
    </>
    );

}
export default CnisEndpoints;